/**
 * Barrier that holds authenticated workspace reads until signup provisioning
 * finishes.
 *
 * `createUser` creates BOTH the `users` row and the account's first workspace
 * (plus a consent-email hop, so it is far from instant). Firebase fires
 * `onAuthStateChanged` as soon as the Google popup resolves — long before that
 * mutation lands — and the auth bootstrap reacts with `router.invalidate()`.
 * That re-runs the route guards, which resolve the landing page from
 * `getWorkspaces`. For a brand-new account the list is still empty, every
 * guard reads "user has no workspace", and the user is bounced to `/`.
 *
 * So the guards must not read workspaces while a signup is provisioning.
 *
 * The barrier is a bare deferred, raised by `beginSignupProvisioning()` and
 * lowered by `endSignupProvisioning()`. It is deliberately NOT tied to the
 * `createUser` mutation's own promise: React Query resolves `mutate`/
 * `mutateAsync` only *after* the mutation's `onSuccess`/`onError` callbacks
 * finish, and those callbacks navigate to a barrier-gated route. Gating on the
 * mutation promise would make the barrier wait on a navigation that waits on
 * the barrier — a self-deadlock that hangs a brand-new user on the login page.
 * Lowering it at the *top* of `onSuccess`/`onError` (once the row + workspace
 * actually exist) breaks that cycle.
 */
let pending: { promise: Promise<void>; resolve: () => void } | null = null;

/**
 * Raise the barrier for a signup that is about to provision. Call this the
 * instant sign-in resolves, before `createUser` goes out, so the invalidated
 * route guards park instead of reading an empty workspace list.
 */
export function beginSignupProvisioning(): void {
  // Defensive: if a prior barrier is somehow still up, release its waiters
  // rather than stranding them behind the new one.
  pending?.resolve();
  let resolve!: () => void;
  const promise = new Promise<void>((r) => {
    resolve = r;
  });
  pending = { promise, resolve };
}

/**
 * Lower the barrier once provisioning has actually finished (the row + first
 * workspace exist). Safe to call when no barrier is up. Must be called on both
 * the success and failure paths so a failed signup never wedges later reads.
 */
export function endSignupProvisioning(): void {
  pending?.resolve();
  pending = null;
}

/** Resolves immediately unless a signup is currently provisioning. */
export async function awaitSignupProvisioning(): Promise<void> {
  await pending?.promise;
}

/** Whether a signup is currently provisioning (barrier raised, not yet lowered). */
export function isSignupProvisioning(): boolean {
  return pending !== null;
}

/**
 * Test-only: drop any barrier left behind by a previous case. Resolves the
 * pending promise first so a waiter left in flight by a failure path (an
 * `awaitSignupProvisioning()` that never saw its `end`) is released rather than
 * stranded forever.
 */
export function resetSignupProvisioning(): void {
  pending?.resolve();
  pending = null;
}
